/**
 * Live WhatsApp message preview (chat bubble) used by Compose and Bulk Send.
 * Renders a template with the operator's uploaded media + entered/sample variable
 * values, or a plain free-text message. Mirrors how the message appears on a phone.
 */

import { ReactNode } from 'react';
import { IconExternalLink, IconPhone, IconArrowBackUp, IconFileText, IconPlayerPlay, IconMapPin } from '@tabler/icons-react';
import { Stack, Group, Text, Image, ThemeIcon } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import type { TemplateListItem } from '../WhatsApp/dialog360.types';
import { parseTemplate } from './whatsappTemplate';

function substitute(text: string, values?: string[]): string {
  if (!values || values.length === 0) return text;
  let out = text;
  values.forEach((v, i) => {
    if (v) out = out.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'), v);
  });
  return out;
}

function ButtonRow({ type, text }: { type: string; text: string }) {
  const icon = type === 'URL' ? <IconExternalLink size={16} /> : type === 'PHONE_NUMBER' ? <IconPhone size={16} /> : <IconArrowBackUp size={16} />;
  return (
    <Group justify="center" gap={6} py={8} style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <ThemeIcon size="sm" variant="transparent" c="#0a7cff">{icon}</ThemeIcon>
      <Text size="sm" fw={500} c="#0a7cff">{text || 'Button'}</Text>
    </Group>
  );
}

export function WhatsAppMessagePreview({
  template,
  mediaUrl,
  bodyValues,
  freeText,
}: {
  template?: TemplateListItem | null;
  mediaUrl?: string | null;
  bodyValues?: string[];
  freeText?: string;
}) {
  const wrap = (children: ReactNode) => (
    <div style={{ background: 'var(--mantine-color-default-hover)', padding: 10, borderRadius: 10 }}>
      <Card withBorder padding={0} radius="md" style={{ maxWidth: 260, marginInline: 'auto', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>
        {children}
      </Card>
    </div>
  );

  if (!template) {
    return wrap(
      <Stack gap={6} p="sm">
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{freeText?.trim() ? freeText : <Text span c="dimmed">Your message preview will appear here…</Text>}</Text>
        <Text size="9px" c="dimmed" ta="right" mt={2}>12:00</Text>
      </Stack>,
    );
  }

  const s = parseTemplate(template);
  const bodyText = substitute(s.bodyText, bodyValues);
  // Fall back to the template's own example media when the operator hasn't uploaded one.
  const headerComp = (template.components ?? []).find((c) => String(c.type).toUpperCase() === 'HEADER');
  const previewMedia = mediaUrl || headerComp?.example?.headerHandle?.[0];

  return wrap(
    <>
      {s.headerFormat === 'IMAGE' && previewMedia && <Image src={previewMedia} h={120} fit="cover" />}
      {s.headerFormat === 'IMAGE' && !previewMedia && (
        <Group justify="center" h={84} bg="gray.1" c="dimmed"><Text size="xs">Image header — upload to preview</Text></Group>
      )}
      {s.headerFormat === 'VIDEO' && (
        <Group justify="center" h={96} bg="dark.5"><ThemeIcon size="lg" variant="transparent" c="white"><IconPlayerPlay size={32} /></ThemeIcon></Group>
      )}
      {s.headerFormat === 'DOCUMENT' && (
        <Group h={64} px="md" bg="gray.1" gap="sm"><ThemeIcon variant="light" color="gray"><IconFileText size={18} /></ThemeIcon><Text size="sm" c="dimmed">Document</Text></Group>
      )}
      {s.headerFormat === 'LOCATION' && (
        <Group h={64} px="md" bg="gray.1" gap="sm"><ThemeIcon variant="light" color="gray"><IconMapPin size={18} /></ThemeIcon><Text size="sm" c="dimmed">Location</Text></Group>
      )}

      <Stack gap={6} p="sm">
        {s.headerFormat === 'TEXT' && s.headerText && <Text size="sm" fw={700}>{s.headerText}</Text>}
        {bodyText && <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{bodyText}</Text>}
        {s.footerText && <Text size="xs" c="dimmed">{s.footerText}</Text>}
        <Text size="9px" c="dimmed" ta="right" mt={2}>12:00</Text>
      </Stack>

      {s.buttons.length > 0 && (
        <Stack gap={0}>
          {s.buttons.map((b, i) => <ButtonRow key={i} type={b.type} text={b.text} />)}
        </Stack>
      )}
    </>,
  );
}
